import { CdkMenu, CdkMenuItemRadio, CdkMenuTrigger } from "@angular/cdk/menu";
import { ConnectedPosition } from "@angular/cdk/overlay";
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  input,
  output,
} from "@angular/core";
import { StatusComponent } from "@corp-ais/omniark-ui";
import { formatFigures } from "../../../utils/price-amount.util";
import { PriceEntry } from "../../../utils/price-entry.util";

/**
 * "Price i of N ▾" with every sibling in a menu, and ‹ › to step through
 * them. Presentational: it emits the chosen id and never navigates, so the
 * shell's `goTo` (and the route guard's prompt) stay the only way a price
 * changes.
 *
 * The menu is a CDK menu, which owns what a popover owes the keyboard:
 * Enter, Space and the arrows open it onto the items, the arrows, Home and
 * End move through them, typing jumps to a title, Enter picks, Escape and
 * Tab close it, and focus returns to the trigger on every exit.
 */
@Component({
  selector: "app-price-switcher",
  imports: [CdkMenu, CdkMenuItemRadio, CdkMenuTrigger, StatusComponent],
  templateUrl: "./price-switcher.html",
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PriceSwitcher {
  readonly entries = input.required<readonly PriceEntry[]>();
  readonly activeId = input.required<string>();
  readonly busy = input(false);

  readonly priceSelect = output<string>();

  readonly active = computed(
    () => this.entries().find((e) => e.id === this.activeId()) ?? null,
  );
  readonly prev = computed(() => {
    const active = this.active();
    return active ? (this.entries()[active.position - 2] ?? null) : null;
  });
  readonly next = computed(() => {
    const active = this.active();
    return active ? (this.entries()[active.position] ?? null) : null;
  });

  readonly positions: ConnectedPosition[] = [
    {
      originX: "start",
      originY: "bottom",
      overlayX: "start",
      overlayY: "top",
      offsetY: 6,
    },
    {
      originX: "start",
      originY: "top",
      overlayX: "start",
      overlayY: "bottom",
      offsetY: -6,
    },
  ];

  figures(entry: PriceEntry): string {
    return formatFigures(entry.figures);
  }

  pick(id: string): void {
    if (id !== this.activeId()) this.priceSelect.emit(id);
  }

  step(entry: PriceEntry | null): void {
    if (entry) this.pick(entry.id);
  }
}
